/**
 * Wrapping and unwrapping the venue's collateral — USDU, B8.
 *
 * **Lazily imported, and it has to stay that way.** Like `funding.ts` this
 * module reaches wagmi's actions, which reach viem: one *static* import of it
 * from the eager graph puts the signing stack on every anonymous visitor's cold
 * load (invariant 13, §F21k). It is reached only from inside `<WalletProvider>`'s
 * own `import()` boundary.
 *
 * ## Why there is a wrapper at all
 *
 * The venue settles in **USDU**, a 1:1 wrapper over native USDC that the
 * exchange owns (`contracts/src/USDU.sol`). The reason is narrow and is about
 * the backend rather than about the user: the balance mirror the matcher reads
 * on every order is `min(balanceOf, allowance)`, and an allowance is **not
 * derivable from ERC-20 events** — `transferFrom` decrements it and emits only
 * `Transfer`. So the mirror could never be rebuilt from a log, every touched
 * account cost two `eth_call`s per poll, and a venue that restarted was blind to
 * every funded account until it next moved money. USDU emits one event carrying
 * the absolute pair, which makes the mirror a pure fold.
 *
 * ## What that costs the user, said plainly here because the screen says it too
 *
 * Three things, and none is hidden:
 *
 * 1. **A wrap step.** USDC does not go straight into a position any more; it is
 *    deposited into USDU first. Exact both ways — same six decimals, no fee, no
 *    rounding.
 * 2. **The custody claim narrowed.** USDU carries admin `block` / `unblock`,
 *    so the exchange multi-sig *can* freeze an account, and it sits behind an
 *    upgradeable proxy the same multi-sig controls. That is a deliberate move
 *    away from full self-custody, and `status()` below exists so a blocked user
 *    is told which state they are in rather than watching an unwrap revert.
 *    (It could also reassign a frozen balance until `docs/06` §F124 retired
 *    `redistribute`.)
 * 3. **Taking money out waits for its markets** (`docs/06` §F76, §F126).
 *    `requestUnwrap` sets the amount aside at once — it leaves the trading
 *    balance, so the venue stops counting it — and the token records
 *    `readyAt`, the end of the last market the money could be in.
 *    `finishUnwrap` pays it out as USDC once `settledThrough()` has reached
 *    `readyAt` — every market it could be in has settled — or once
 *    `WITHDRAWAL_BACKSTOP` (48 hours) has passed regardless. `cancelUnwrap`
 *    puts it back at any time before that. The wait exists so a trade the
 *    venue has already matched still settles: a trader who could empty their
 *    wallet between a match and its settlement could back out of a loss. What
 *    is in no market need not wait, and the venue's relayer pays that part at
 *    once (`relayWithdrawal`, `payPending`) — the chain cannot tell which part
 *    that is, which is why only the relayer may. For the same reason as the
 *    wait, USDU cannot be sent to another wallet, and the exchange's allowance
 *    can only go up (since gasless funding a deposit allows it without limit —
 *    `chain-targets.ts`). Until §F126 the wait was a fixed `UNWRAP_DELAY` of
 *    two minutes from the request.
 *
 * **Every address comes from `GET /config`** — `chain.collateral_address` — for
 * `chain-targets.ts`'s reason: a client that hardcoded one would wrap the wrong
 * token on any chain but the one it was built for.
 */

import type { PendingUnwrap } from '@/domain/unwrap'
import type { WalletConfig } from './connectors'
import { feeCaps } from './fees'

const USDU_ABI = [
  {
    type: 'function',
    name: 'deposit',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'requestUnwrap',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'finishUnwrap',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'cancelUnwrap',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: [],
  },
  {
    type: 'function',
    name: 'pendingUnwrap',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [
      { name: 'amount', type: 'uint256' },
      { name: 'readyAt', type: 'uint256' },
    ],
  },
  {
    type: 'function',
    name: 'settledThrough',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'WITHDRAWAL_BACKSTOP',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  // The refusals a user can meet here, so a revert arrives with its name and
  // `<WalletProvider>`'s `messageFor` can say it in a sentence rather than
  // showing a four-byte selector.
  {
    type: 'error',
    name: 'UnwrapNotReady',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'readyAt', type: 'uint256' },
      { name: 'chainNow', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'AwaitingSettlement',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'readyAt', type: 'uint256' },
      { name: 'settledThrough', type: 'uint256' },
    ],
  },
  { type: 'error', name: 'NothingPending', inputs: [{ name: 'account', type: 'address' }] },
  { type: 'error', name: 'AccountBlocked', inputs: [{ name: 'account', type: 'address' }] },
  // The relayer's own refusals (§F126). No call from this client can meet them —
  // `relayWithdrawal` and `payPending` are the relayer's — and they are here so
  // a revert anybody pastes from a trace decodes to a name.
  { type: 'error', name: 'NotRelayer', inputs: [] },
  {
    type: 'error',
    name: 'BadInstant',
    inputs: [
      { name: 'amount', type: 'uint256' },
      { name: 'instant', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'PendingChanged',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'have', type: 'uint256' },
      { name: 'want', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'ExceedsPending',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'pending', type: 'uint256' },
      { name: 'amount', type: 'uint256' },
    ],
  },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'status',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint8' }],
  },
  {
    type: 'function',
    name: 'usdc',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
] as const

const ERC20_ABI = [
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * `USDU.Status`, in the contract's own order.
 *
 * Two members since `docs/06` §F124 retired the third, the terminal
 * `REDISTRIBUTED` that no account ever held. A `2` read from the chain is
 * therefore a member this build does not know, and is `UNKNOWN` like any other.
 */
export type AccountStatus = 'NORMAL' | 'BLOCKED' | 'UNKNOWN'

const STATUS_BY_INDEX: readonly AccountStatus[] = ['NORMAL', 'BLOCKED']

/**
 * What the money screen needs, read in one pass.
 *
 * Every field is nullable and `null` means **unread**, never zero — the same
 * three-valued discipline `domain/trading-setup.ts` keeps, and for the same reason:
 * an RPC that timed out has not told us the user has no money, and rendering it
 * as zero invites them to deposit funds they already have.
 */
export interface WrapperState {
  /** Native USDC in the wallet — what is available to wrap. */
  backing: bigint | null
  /** USDU in the wallet — what the venue can see. */
  collateral: bigint | null
  /** How much USDC the wrapper may pull. `deposit` needs this first. */
  wrapperAllowance: bigint | null
  /** The account's standing on the collateral token. */
  status: AccountStatus
  /** The backing token's address, read from the wrapper itself. */
  backingAddress: `0x${string}` | null
  /** The unwrap in flight, if any. */
  pendingUnwrap: PendingUnwrap | null
  /**
   * `USDU.settledThrough()` times a thousand: the instant up to which every
   * market has settled, which a pending unwrap's `readyAt` waits for (§F126).
   * Zero is the token's own answer when the exchange cannot say — every
   * withdrawal then waits for the backstop — and is not `null`, unread.
   */
  settledThroughMs: number | null
  /**
   * `USDU.WITHDRAWAL_BACKSTOP()` times a thousand: past `readyAt` by this
   * much, a withdrawal pays whether or not its markets settled. Read from the
   * token rather than written here — "read it from the payload", for a number
   * that is the contract's.
   */
  backstopMs: number | null
  /**
   * The block every read above was pinned to, or `null` for `latest` — whose
   * block the wallet does not say, and which MetaMask may answer from a cached
   * one. What lets a pushed change tell whether it is newer than what is on
   * screen (`domain/unwrap.ts` `withdrawalReadBlock`).
   */
  readAtBlock: bigint | null
}

export const UNREAD_WRAPPER: WrapperState = {
  backing: null,
  collateral: null,
  wrapperAllowance: null,
  status: 'UNKNOWN',
  backingAddress: null,
  pendingUnwrap: null,
  settledThroughMs: null,
  backstopMs: null,
  readAtBlock: null,
}

/**
 * Read everything the money screen renders.
 *
 * **The backing token's address comes from the wrapper**, not from `/config`.
 * `/config.chain` publishes the *collateral* address and nothing about what
 * backs it, and inventing a second config field for a value the contract already
 * knows would be two places one address can be wrong (§1.5's rule pointed the
 * other way). One `eth_call`, cached by the caller.
 *
 * Each read fails independently into `null`. A partial answer is still worth
 * rendering — a user whose `balanceOf` came back can be told what they hold even
 * if the allowance read timed out.
 *
 * **`atBlock` pins every read to one block**, and it is how a change the venue
 * pushed is read: MetaMask answers `latest` from a block it cached, measured
 * five seconds and more behind a local node (2026-10-07), while a read naming
 * the block the venue saw the change in is answered fresh. Without it, `latest`.
 */
export async function readWrapper(
  config: WalletConfig,
  account: `0x${string}`,
  collateralAddress: `0x${string}`,
  atBlock?: bigint,
): Promise<WrapperState> {
  const { readContract } = await import('wagmi/actions')
  const at = atBlock === undefined ? {} : { blockNumber: atBlock }

  const backingAddress = await readContract(config, {
    address: collateralAddress,
    abi: USDU_ABI,
    functionName: 'usdc',
    ...at,
  }).catch(() => null)

  const [collateral, status, backing, wrapperAllowance, pendingUnwrap, settledThrough, backstop] = await Promise.all([
    readContract(config, {
      address: collateralAddress,
      abi: USDU_ABI,
      functionName: 'balanceOf',
      args: [account],
      ...at,
    }).catch(() => null),
    readContract(config, {
      address: collateralAddress,
      abi: USDU_ABI,
      functionName: 'status',
      args: [account],
      ...at,
    }).catch(() => null),
    backingAddress ?
      readContract(config, {
        address: backingAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [account],
        ...at,
      }).catch(() => null)
    : Promise.resolve(null),
    backingAddress ?
      readContract(config, {
        address: backingAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [account, collateralAddress],
        ...at,
      }).catch(() => null)
    : Promise.resolve(null),
    readContract(config, {
      address: collateralAddress,
      abi: USDU_ABI,
      functionName: 'pendingUnwrap',
      args: [account],
      ...at,
    }).catch(() => null),
    // Both §F126's: what a pending withdrawal's `readyAt` is compared with.
    // `settledThrough` moves with every market settled, so it is read beside
    // the withdrawal every time rather than once.
    readContract(config, {
      address: collateralAddress,
      abi: USDU_ABI,
      functionName: 'settledThrough',
      ...at,
    }).catch(() => null),
    readContract(config, {
      address: collateralAddress,
      abi: USDU_ABI,
      functionName: 'WITHDRAWAL_BACKSTOP',
      ...at,
    }).catch(() => null),
  ])

  return {
    ...assembleWrapper({
      backingAddress,
      collateral,
      status,
      backing,
      wrapperAllowance,
      pendingUnwrap,
      settledThrough,
      backstop,
    }),
    // Claimed only if the withdrawal itself was read there: a pinned read the
    // wallet's provider could not answer yet (a block behind the venue's) has
    // not seen that block, and claiming it would stop the retry.
    readAtBlock: atBlock !== undefined && pendingUnwrap !== null ? atBlock : null,
  }
}

/**
 * The four raw answers, narrowed into a `WrapperState`.
 *
 * **Split out from `readWrapper` so it can be tested without a chain**, which is
 * this repository's own grain: `readWrapper` is the I/O shell and this is the
 * part with a decision in it. Most unit tests here mock nothing, and
 * `chain-targets.ts` draws the same line — its test covers `targetsFrom` and
 * leaves a successful read to the chain.
 *
 * Every narrowing is `typeof … : null`, and the `null` is the point. A value
 * that is not the type expected did not arrive; rendering it as `0` would tell
 * a user with money that they have none.
 */
export function assembleWrapper(raw: {
  backingAddress: unknown
  collateral: unknown
  status: unknown
  backing: unknown
  wrapperAllowance: unknown
  pendingUnwrap?: unknown
  settledThrough?: unknown
  backstop?: unknown
}): WrapperState {
  return {
    backing: typeof raw.backing === 'bigint' ? raw.backing : null,
    collateral: typeof raw.collateral === 'bigint' ? raw.collateral : null,
    wrapperAllowance: typeof raw.wrapperAllowance === 'bigint' ? raw.wrapperAllowance : null,
    status: statusOf(raw.status),
    backingAddress:
      typeof raw.backingAddress === 'string' && /^0x[0-9a-fA-F]{40}$/.test(raw.backingAddress) ?
        (raw.backingAddress as `0x${string}`)
      : null,
    pendingUnwrap: pendingOf(raw.pendingUnwrap),
    settledThroughMs: secondsToMs(raw.settledThrough),
    backstopMs: secondsToMs(raw.backstop),
    readAtBlock: null,
  }
}

/**
 * Chain seconds (`uint256`) → integer milliseconds, or `null` for a read that
 * did not arrive or would not survive the crossing into a `number`.
 */
function secondsToMs(raw: unknown): number | null {
  if (typeof raw !== 'bigint' || raw < 0n || raw * 1000n > BigInt(Number.MAX_SAFE_INTEGER)) return null
  return Number(raw) * 1000
}

/**
 * `pendingUnwrap(account)` → a `PendingUnwrap`, or `null` for a read that did
 * not arrive. viem returns a multi-output view as a tuple.
 */
function pendingOf(raw: unknown): PendingUnwrap | null {
  if (!Array.isArray(raw) || raw.length !== 2) return null
  const [amount, readyAt] = raw as unknown[]
  if (typeof amount !== 'bigint' || typeof readyAt !== 'bigint') return null
  if (readyAt * 1000n > BigInt(Number.MAX_SAFE_INTEGER)) return null
  return { amount, readyAtMs: Number(readyAt) * 1000 }
}

/** `uint8` → the name, or `UNKNOWN` for a read that failed or a member this build does not know. */
export function statusOf(raw: unknown): AccountStatus {
  const index = typeof raw === 'number' ? raw : typeof raw === 'bigint' ? Number(raw) : -1
  return STATUS_BY_INDEX[index] ?? 'UNKNOWN'
}

/**
 * Let the wrapper pull `amount` micro-USDC.
 *
 * **Bounded, never infinite** — USDC is never approved without limit, even
 * though adding funds now allows *settlement* without limit (`GASLESS-PLAN.md`
 * decision 3, which is about the trading balance only). There is
 * deliberately no path through this module that can express `MaxUint256`, so it
 * cannot be asked for in a hurry. The amount is what the user is about to
 * deposit and no more.
 */
export async function approveBacking(
  config: WalletConfig,
  backingAddress: `0x${string}`,
  collateralAddress: `0x${string}`,
  amountMicroUsdc: bigint,
): Promise<`0x${string}`> {
  if (amountMicroUsdc <= 0n) throw new Error('approve: the amount must be positive')
  const { readContract, writeContract } = await import('wagmi/actions')

  // **The wrapper is asked to confirm its own backing token before it is given
  // an allowance on one.** `backingAddress` reaches here from `readWrapper`,
  // which read it from this same contract — so re-reading it costs one
  // `eth_call` and closes the gap between the two: a caller that passed a
  // remembered address, an address from a different chain, or an address the
  // panel rendered before the user switched networks would otherwise have the
  // user approve a token the wrapper cannot spend, or worse, one somebody else
  // can. `usdc()` is the only source with authority here — `/config.chain`
  // publishes the collateral and says nothing about what backs it (§F48d).
  const declared = await readContract(config, {
    address: collateralAddress,
    abi: USDU_ABI,
    functionName: 'usdc',
  }).catch(() => null)
  if (typeof declared !== 'string' || declared.toLowerCase() !== backingAddress.toLowerCase()) {
    throw new Error(
      'The collateral contract does not name this token as its backing. Nothing was approved.',
    )
  }

  return writeContract(config, {
    ...(await feeCaps(config)),
    address: backingAddress,
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [collateralAddress, amountMicroUsdc],
  })
}

/** Wrap `amount` micro-USDC into the same number of micro-USDU. Exact. */
export async function deposit(
  config: WalletConfig,
  collateralAddress: `0x${string}`,
  amountMicroUsdc: bigint,
): Promise<`0x${string}`> {
  if (amountMicroUsdc <= 0n) throw new Error('deposit: the amount must be positive')
  const { writeContract } = await import('wagmi/actions')
  return writeContract(config, {
    ...(await feeCaps(config)),
    address: collateralAddress,
    abi: USDU_ABI,
    functionName: 'deposit',
    args: [amountMicroUsdc],
  })
}

/**
 * Ask to unwrap `amount` micro-USDU (§F76). It leaves the trading balance in
 * this transaction and is paid out as USDC by `finishUnwrap` once the markets
 * it could be in have settled (§F126) — or sooner, for the part in no market,
 * by the venue's relayer. A second request adds to the first and moves the
 * wait for the whole amount to the new request's markets.
 *
 * Reverts on chain unless the account is `NORMAL`. The screen disables the
 * control and explains, rather than letting a blocked user watch a wallet
 * confirmation fail with `AccountBlocked` and no sentence attached.
 */
export async function requestUnwrap(
  config: WalletConfig,
  collateralAddress: `0x${string}`,
  amountMicroUsdc: bigint,
): Promise<`0x${string}`> {
  if (amountMicroUsdc <= 0n) throw new Error('unwrap: the amount must be positive')
  const { writeContract } = await import('wagmi/actions')
  return writeContract(config, {
    ...(await feeCaps(config)),
    address: collateralAddress,
    abi: USDU_ABI,
    functionName: 'requestUnwrap',
    args: [amountMicroUsdc],
  })
}

/**
 * Pay out `account`'s pending unwrap as USDC. The contract lets anybody call
 * it — the money goes to `account` and nowhere else — so this is the user's
 * own wallet finishing its own unwrap. Refused `AwaitingSettlement` until its
 * markets have settled, unless the backstop has passed.
 */
export async function finishUnwrap(
  config: WalletConfig,
  collateralAddress: `0x${string}`,
  account: `0x${string}`,
): Promise<`0x${string}`> {
  const { writeContract } = await import('wagmi/actions')
  return writeContract(config, {
    ...(await feeCaps(config)),
    address: collateralAddress,
    abi: USDU_ABI,
    functionName: 'finishUnwrap',
    args: [account],
  })
}

/** Put a pending unwrap back into the trading balance, whole. Allowed at any time before it is finished. */
export async function cancelUnwrap(
  config: WalletConfig,
  collateralAddress: `0x${string}`,
): Promise<`0x${string}`> {
  const { writeContract } = await import('wagmi/actions')
  return writeContract(config, {
    ...(await feeCaps(config)),
    address: collateralAddress,
    abi: USDU_ABI,
    functionName: 'cancelUnwrap',
    args: [],
  })
}
